/**
 * Phase 4 PR H1-A — public practice-questions interactivity.
 *
 * Two responsibilities (matching legacy /education/practicequestions/):
 *   1. Per-question "Show Answer" reveal — click the inline text link;
 *      adds .amri-education-question--answer-correct stays + reveal the
 *      hidden "The best answer is: X" panel + hides the Show Answer link.
 *      Pattern mirrors legacy js-answer-toggle + answer-highlight.
 *   2. 3-way segmented tab switch (ACLS / PALS / BLS) — server-renders all
 *      three sections; JS toggles their `hidden` state, swaps the H1 prefix
 *      label, and persists the active selection in localStorage so
 *      refresh / back-button preserves the user's choice.
 *
 * Markup contract (Views/Education/PracticeQuestions.cshtml +
 * _QuestionAnswerList.cshtml + RandomPracticeQuestion.cshtml):
 *   - data-amri-practice-exam-switch="acls|pals|bls"   — tab button
 *   - data-amri-practice-exam-section="acls|pals|bls"  — section element
 *   - data-amri-practice-exam-label                     — H1 prefix span
 *   - data-amri-question[data-question-id]              — <li> per question
 *   - data-amri-answer-reveal                            — Show Answer link
 *   - data-amri-answer-best                              — hidden best-answer panel
 *   - data-amri-answer-label[data-answer-id][data-correct]  — per-answer <label>
 */

const EXAM_STORAGE_KEY = 'amri-education-practice:active-exam';
const DEFAULT_EXAM_SLUG = 'acls';
const CORRECT_HIGHLIGHT_CLASS = 'amri-education-question--answer-highlight';

function setActiveExam(slug: string): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('[data-amri-practice-exam-switch]');
    const sections = document.querySelectorAll<HTMLElement>('[data-amri-practice-exam-section]');
    const examLabel = document.querySelector<HTMLElement>('[data-amri-practice-exam-label]');

    if (buttons.length === 0 || sections.length === 0) {
        return;
    }

    let activeLabel = slug.toUpperCase();

    buttons.forEach((btn) => {
        const isActive = btn.getAttribute('data-amri-practice-exam-switch') === slug;
        btn.classList.toggle('amri-education-practice--exam-switch-button--active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
            const labelOverride = btn.getAttribute('data-exam-label');
            if (labelOverride) {
                activeLabel = labelOverride;
            }
        }
    });

    sections.forEach((section) => {
        const sectionSlug = section.getAttribute('data-amri-practice-exam-section');
        if (sectionSlug === slug) {
            section.removeAttribute('hidden');
        } else {
            section.setAttribute('hidden', '');
        }
    });

    if (examLabel) {
        examLabel.textContent = activeLabel;
    }

    try {
        window.localStorage.setItem(EXAM_STORAGE_KEY, slug);
    } catch {
        // localStorage may throw in private-browsing mode; the UI still
        // works for the current page-load.
    }
}

function wireExamSwitch(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('[data-amri-practice-exam-switch]');
    if (buttons.length === 0) {
        return;
    }

    buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const slug = btn.getAttribute('data-amri-practice-exam-switch');
            if (slug) {
                setActiveExam(slug);
            }
        });
    });

    let storedSlug: string | null = null;
    try {
        storedSlug = window.localStorage.getItem(EXAM_STORAGE_KEY);
    } catch {
        // ignore — fall back to default
    }

    setActiveExam(storedSlug ?? DEFAULT_EXAM_SLUG);
}

function wireRevealLinks(): void {
    const links = document.querySelectorAll<HTMLAnchorElement>('[data-amri-answer-reveal]');
    links.forEach((link) => {
        link.addEventListener('click', (event) => {
            event.preventDefault();

            const question = link.closest<HTMLElement>('[data-amri-question]');
            if (!question) {
                return;
            }

            // (1) Highlight the correct answer's label (yellow/cream bg).
            const correctLabel = question.querySelector<HTMLLabelElement>('[data-amri-answer-label][data-correct="true"]');
            correctLabel?.classList.add(CORRECT_HIGHLIGHT_CLASS);

            // (2) For the user's currently-selected radio (if any), mark
            // its per-radio status span (the reusable answer-status
            // element shared with case-study) as correct/incorrect.
            const selectedRadio = question.querySelector<HTMLInputElement>('input[type="radio"][data-amri-answer]:checked');
            if (selectedRadio) {
                const selectedAnswerId = selectedRadio.getAttribute('data-answer-id');
                const selectedLabel = question.querySelector<HTMLLabelElement>(
                    `[data-amri-answer-label][data-answer-id="${selectedAnswerId}"]`
                );
                const wasCorrect = selectedLabel?.getAttribute('data-correct') === 'true';
                const statusSpan = selectedLabel?.querySelector<HTMLElement>('[data-amri-answer-status]');
                if (statusSpan) {
                    statusSpan.classList.toggle('amri-education-answer-status--correct', wasCorrect);
                    statusSpan.classList.toggle('amri-education-answer-status--incorrect', !wasCorrect);
                }
            }

            // (3) Reveal the "The best answer is:" panel.
            const best = question.querySelector<HTMLElement>('[data-amri-answer-best]');
            best?.removeAttribute('hidden');

            // (4) Hide the Show Answer link (legacy parity — link
            // disappears, not toggles).
            link.setAttribute('hidden', '');
            link.setAttribute('aria-expanded', 'true');
        });
    });
}

function init(): void {
    wireExamSwitch();
    wireRevealLinks();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

export {};
